<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div>
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        {{ isEdit ? 'Edit Subscription Plan' : 'Create Subscription Plan' }}
      </h1>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
        {{ isEdit ? 'Update plan details and pricing' : 'Create a new subscription plan for your platform' }}
      </p>
    </div>

    <!-- Form -->
    <form @submit.prevent="submitForm" class="space-y-6">
      <!-- Basic Information -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            Basic Information
          </h3>

          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <!-- Plan Name -->
            <div class="sm:col-span-2">
              <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Plan Name <span class="text-red-500">*</span>
              </label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                required
                @input="generateSlug"
                class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="e.g., Professional Plan"
              />
              <p v-if="errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.name }}</p>
            </div>

            <!-- Slug -->
            <div class="sm:col-span-2">
              <label for="slug" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Slug <span class="text-red-500">*</span>
              </label>
              <input
                id="slug"
                v-model="form.slug"
                type="text"
                required
                class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="e.g., professional-plan"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">URL-friendly identifier for this plan</p>
              <p v-if="errors.slug" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.slug }}</p>
            </div>

            <!-- Description -->
            <div class="sm:col-span-2">
              <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                id="description"
                v-model="descriptionText"
                rows="3"
                class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Brief description of what's included in this plan"
              ></textarea>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Enter each feature on a new line</p>
            </div>

            <!-- Sort Order -->
            <div>
              <label for="sort_order" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Display Order <span class="text-red-500">*</span>
              </label>
              <input
                id="sort_order"
                v-model.number="form.sort_order"
                type="number"
                min="0"
                required
                class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Lower numbers appear first</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Pricing -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            Pricing
          </h3>

          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <!-- Monthly Price -->
            <div>
              <label for="price_monthly" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Monthly Price (£) <span class="text-red-500">*</span>
              </label>
              <input
                id="price_monthly"
                v-model.number="form.price_monthly"
                type="number"
                step="0.01"
                min="0"
                required
                class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p v-if="errors.price_monthly" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.price_monthly }}</p>
            </div>

            <!-- Annual Price -->
            <div>
              <label for="price_annual" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Annual Price (£)
              </label>
              <input
                id="price_annual"
                v-model.number="form.price_annual"
                type="number"
                step="0.01"
                min="0"
                class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p v-if="annualSavings" class="mt-1 text-xs text-green-600 dark:text-green-400">
                Saves £{{ annualSavings.toFixed(2) }} ({{ annualSavingsPercent.toFixed(1) }}%) compared to monthly
              </p>
              <p v-if="errors.price_annual" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.price_annual }}</p>
            </div>

            <!-- Billing Period -->
            <div>
              <label for="billing_period" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Billing Period <span class="text-red-500">*</span>
              </label>
              <select
                id="billing_period"
                v-model="form.billing_period"
                required
                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <!-- Trial Days -->
            <div>
              <label for="trial_days" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Trial Days <span class="text-red-500">*</span>
              </label>
              <input
                id="trial_days"
                v-model.number="form.trial_days"
                type="number"
                min="0"
                required
                class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Number of free trial days (0 for no trial)</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Stripe Integration -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            Stripe Integration
          </h3>

          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <!-- Monthly Price ID -->
            <div>
              <label for="stripe_price_id_monthly" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Stripe Monthly Price ID
              </label>
              <input
                id="stripe_price_id_monthly"
                v-model="form.stripe_price_id_monthly"
                type="text"
                class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="price_xxxxxxxxxxxxx"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">From your Stripe Dashboard</p>
            </div>

            <!-- Annual Price ID -->
            <div>
              <label for="stripe_price_id_annual" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Stripe Annual Price ID
              </label>
              <input
                id="stripe_price_id_annual"
                v-model="form.stripe_price_id_annual"
                type="text"
                class="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="price_xxxxxxxxxxxxx"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">From your Stripe Dashboard</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Settings -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            Settings
          </h3>

          <div class="space-y-4">
            <!-- Active Status -->
            <div class="flex items-center justify-between">
              <div>
                <label for="is_active" class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active Status
                </label>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  Only active plans are available for new subscriptions
                </p>
              </div>
              <button
                type="button"
                @click="form.is_active = !form.is_active"
                :class="[
                  form.is_active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700',
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                ]"
              >
                <span
                  :class="[
                    form.is_active ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                  ]"
                />
              </button>
            </div>

            <!-- Public Status -->
            <div class="flex items-center justify-between">
              <div>
                <label for="is_public" class="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Public Visibility
                </label>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  Public plans are shown on the pricing page
                </p>
              </div>
              <button
                type="button"
                @click="form.is_public = !form.is_public"
                :class="[
                  form.is_public ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700',
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                ]"
              >
                <span
                  :class="[
                    form.is_public ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                  ]"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="flex items-center justify-end space-x-3">
        <Link
          :href="route('site-owner.subscriptions.plans.index')"
          class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </Link>
        <button
          type="submit"
          :disabled="form.processing"
          class="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ form.processing ? 'Saving...' : (isEdit ? 'Update Plan' : 'Create Plan') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Link, useForm } from '@inertiajs/vue3';
import route from '@/Utils/route';

const props = defineProps({
  plan: {
    type: Object,
    default: null,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
});

const form = useForm({
  name: props.plan?.name || '',
  slug: props.plan?.slug || '',
  description: props.plan?.description || null,
  price_monthly: props.plan?.price_monthly || 0,
  price_annual: props.plan?.price_annual || null,
  billing_period: props.plan?.billing_period || 'monthly',
  trial_days: props.plan?.trial_days || 0,
  is_active: props.plan?.is_active ?? true,
  is_public: props.plan?.is_public ?? true,
  sort_order: props.plan?.sort_order || 0,
  stripe_price_id_monthly: props.plan?.stripe_price_id_monthly || '',
  stripe_price_id_annual: props.plan?.stripe_price_id_annual || '',
});

// Description text for textarea (converting array to text)
const descriptionText = ref('');
if (props.plan?.description && Array.isArray(props.plan.description)) {
  descriptionText.value = props.plan.description.join('\n');
}

// Watch description text and convert to array
watch(descriptionText, (newValue) => {
  if (newValue) {
    form.description = newValue.split('\n').filter(line => line.trim() !== '');
  } else {
    form.description = null;
  }
});

// Generate slug from name
const generateSlug = () => {
  if (!props.isEdit || !props.plan?.slug) {
    form.slug = form.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
};

// Calculate annual savings
const annualSavings = computed(() => {
  if (form.price_annual && form.price_monthly) {
    const monthlyTotal = form.price_monthly * 12;
    return monthlyTotal - form.price_annual;
  }
  return 0;
});

const annualSavingsPercent = computed(() => {
  if (annualSavings.value && form.price_monthly) {
    return (annualSavings.value / (form.price_monthly * 12)) * 100;
  }
  return 0;
});

const submitForm = () => {
  if (props.isEdit) {
    form.put(route('site-owner.subscriptions.plans.update', props.plan.id));
  } else {
    form.post(route('site-owner.subscriptions.plans.store'));
  }
};
</script>
